/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import Device from '@ion/core/src/services/Device';
import {mergeLatestUpdates} from '@/store/helpers';
import {globalGroupIdSelector} from '@/store/selectors/global-group-chooser-selectors';
import {
  appliedSavedFiltersSelector,
  assetsListSelector,
  assetsUpdatesSelector,
  isLargeFleetModeSelector,
  scDevicesSelector,
  scDevicesUpdatesSelector
} from '@/store/selectors/tracking-selectors';

/**
 * The useAssetState hook is used to keep an array of assets updated,
 * this state stores assets and devices, also maintain their respective updates
 *
 *
 * ```typescript
 *
 *   import {useAssetState} from './hook/tracking/useAssetState';
 *
 *   const assetList = useAssetState();
 *
 * ```
 */

export const useAssetState = () => {
  const [assetList, setAssetList] = useState<Device[]>([]);
  
  const globalGroupId    = useSelector(globalGroupIdSelector);
  const isLargeFleetMode = useSelector(isLargeFleetModeSelector);
  const assets           = useSelector(assetsListSelector);
  const assetsUpdates    = useSelector(assetsUpdatesSelector);
  const scDevices        = useSelector(scDevicesSelector);
  const scDevicesUpdates = useSelector(scDevicesUpdatesSelector);
  const savedFilters     = useSelector(appliedSavedFiltersSelector);

  // Defines a utility to reset the state
  const resetState = useCallback(() => {
    setAssetList([]);
  }, []);

  // Resetting local states when GGC, the tracking mode or Save Filters changes
  useEffect(resetState, [isLargeFleetMode, globalGroupId, savedFilters]);

  useEffect(() => {
    setAssetList([...(assets || []), ...(scDevices || [])]);
  }, [assets, scDevices]);

  useEffect(() => {
    setAssetList((prevAssetList) => mergeLatestUpdates(prevAssetList, assetsUpdates));
  }, [assetsUpdates]);

  useEffect(() => {
    setAssetList((prevAssetList) => mergeLatestUpdates(prevAssetList, scDevicesUpdates));
  }, [scDevicesUpdates]);

  return assetList;
};

export default useAssetState;
